import { useCallback, useEffect, useRef, useState } from "react";
import {
  createOverlayPipEffect,
  EffectPreset,
  EffectRow,
  initEffectUpload,
  listEffects,
  uploadToPresignedUrl,
} from "../api";

const PRESETS: { id: EffectPreset; label: string }[] = [
  { id: "bottom-left", label: "Bas gauche" },
  { id: "bottom-center", label: "Bas centré (défaut)" },
  { id: "bottom-right", label: "Bas droite" },
];

interface VideoPickerProps {
  label: string;
  hint?: string;
  value: string;
  onChange: (key: string) => void;
}

function VideoPicker({ label, hint, value, onChange }: VideoPickerProps) {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const fileInput = useRef<HTMLInputElement | null>(null);

  const handleFile = useCallback(
    async (file: File) => {
      setUploading(true);
      setError(null);
      setProgress(0);
      try {
        const init = await initEffectUpload(
          file.name,
          file.type || "video/mp4"
        );
        await uploadToPresignedUrl(
          init.uploadUrl,
          file,
          file.type || "video/mp4",
          setProgress
        );
        onChange(init.s3Key);
      } catch (e) {
        setError(e instanceof Error ? e.message : "Upload échoué");
      } finally {
        setUploading(false);
      }
    },
    [onChange]
  );

  return (
    <div className="space-y-1.5">
      <label className="block text-sm font-medium text-charcoal">{label}</label>
      <div className="flex gap-2">
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="effects-inputs/<id>.mp4 ou colle un S3 key existant"
          className="flex-1 rounded-md border border-stone bg-white px-3 py-2 font-mono text-xs text-charcoal focus:border-charcoal focus:outline-none"
        />
        <button
          type="button"
          onClick={() => fileInput.current?.click()}
          disabled={uploading}
          className="whitespace-nowrap rounded-md bg-charcoal px-3 py-2 text-sm font-medium text-ivory hover:bg-black disabled:opacity-50"
        >
          {uploading ? `Upload ${progress}%` : "Upload"}
        </button>
        <input
          ref={fileInput}
          type="file"
          accept="video/*"
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) void handleFile(f);
            e.target.value = ""; // allow re-selecting same file
          }}
        />
      </div>
      {hint && !error && <div className="text-xs text-concrete">{hint}</div>}
      {error && <div className="text-xs text-red-600">{error}</div>}
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const cls =
    status === "ready"
      ? "bg-lemonade text-black"
      : status === "error"
      ? "bg-red-600 text-ivory"
      : status === "processing"
      ? "bg-blue-500 text-ivory"
      : "bg-stone text-charcoal";
  return (
    <span
      className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${cls}`}
    >
      {status}
    </span>
  );
}

export default function AdminEffects() {
  const [mainKey, setMainKey] = useState("");
  const [overlayKey, setOverlayKey] = useState("");
  const [preset, setPreset] = useState<EffectPreset>("bottom-center");
  const [startTime, setStartTime] = useState(3);
  const [duration, setDuration] = useState(12);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const [effects, setEffects] = useState<EffectRow[]>([]);
  const [listError, setListError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    try {
      const rows = await listEffects();
      setEffects(rows);
      setListError(null);
    } catch (e) {
      setListError(e instanceof Error ? e.message : "Liste indisponible");
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  // Poll every 4s while at least one effect is non-terminal.
  useEffect(() => {
    const pending = effects.some(
      (e) => e.status === "queued" || e.status === "processing"
    );
    if (!pending) return;
    const t = setInterval(() => void reload(), 4000);
    return () => clearInterval(t);
  }, [effects, reload]);

  const submit = async () => {
    if (!mainKey.trim() || !overlayKey.trim()) {
      setSubmitError("Renseigne les 2 vidéos (upload ou S3 key)");
      return;
    }
    setSubmitting(true);
    setSubmitError(null);
    try {
      await createOverlayPipEffect({
        mainVideoKey: mainKey.trim(),
        overlayVideoKey: overlayKey.trim(),
        preset,
        startTime,
        duration,
      });
      setMainKey("");
      setOverlayKey("");
      void reload();
    } catch (e) {
      setSubmitError(e instanceof Error ? e.message : "Échec du compose");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-8">
      <section className="rounded-lg border border-stone bg-white p-4 md:p-6">
        <h2 className="text-lg font-bold text-charcoal">
          Nouvel effet — Overlay PIP
        </h2>
        <p className="mt-1 text-sm text-concrete">
          Une miniature glisse dans la vidéo principale pendant N secondes.
          L&apos;audio de la miniature est mute, ta voix continue.
        </p>

        <div className="mt-4 space-y-4">
          <VideoPicker
            label="Vidéo principale (où tu parles)"
            value={mainKey}
            onChange={setMainKey}
          />
          <VideoPicker
            label="Vidéo overlay (miniature)"
            value={overlayKey}
            onChange={setOverlayKey}
          />

          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <div>
              <label className="mb-2 block text-sm font-medium text-charcoal">
                Position
              </label>
              <div className="space-y-1">
                {PRESETS.map((p) => (
                  <label key={p.id} className="flex items-center gap-2 text-sm">
                    <input
                      type="radio"
                      name="preset"
                      checked={preset === p.id}
                      onChange={() => setPreset(p.id)}
                    />
                    {p.label}
                  </label>
                ))}
              </div>
            </div>
            <div>
              <label className="mb-2 block text-sm font-medium text-charcoal">
                Début (s)
              </label>
              <input
                type="number"
                value={startTime}
                onChange={(e) =>
                  setStartTime(Math.max(0, parseFloat(e.target.value) || 0))
                }
                min={0}
                step={0.5}
                className="w-full rounded-md border border-stone bg-white px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="mb-2 block text-sm font-medium text-charcoal">
                Durée visible (s)
              </label>
              <input
                type="number"
                value={duration}
                onChange={(e) =>
                  setDuration(Math.max(2, parseFloat(e.target.value) || 0))
                }
                min={2}
                step={0.5}
                className="w-full rounded-md border border-stone bg-white px-3 py-2 text-sm"
              />
            </div>
          </div>

          {submitError && (
            <div className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">
              {submitError}
            </div>
          )}

          <button
            type="button"
            onClick={() => void submit()}
            disabled={submitting}
            className="rounded-md bg-charcoal px-4 py-2 text-sm font-semibold text-ivory hover:bg-black disabled:opacity-50"
          >
            {submitting ? "Envoi…" : "Composer"}
          </button>
        </div>
      </section>

      <section>
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-lg font-bold text-charcoal">Historique</h2>
          <button
            type="button"
            onClick={() => void reload()}
            className="text-xs text-concrete hover:text-charcoal"
          >
            Rafraîchir
          </button>
        </div>

        {listError && (
          <div className="mb-3 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">
            {listError}
          </div>
        )}

        {effects.length === 0 && !listError && (
          <p className="text-sm text-concrete">
            Aucun effet pour le moment. Compose le premier ci-dessus.
          </p>
        )}

        <ul className="space-y-3">
          {effects.map((e) => (
            <li
              key={e.effectId}
              className="rounded-lg border border-stone bg-white p-4"
            >
              <div className="mb-2 flex items-start justify-between gap-3">
                <div>
                  <div className="font-mono text-xs text-concrete">
                    {e.effectId.slice(0, 16)}…
                  </div>
                  <div className="text-xs text-concrete">
                    {new Date(e.createdAt).toLocaleString("fr-FR")}
                  </div>
                </div>
                <StatusBadge status={e.status} />
              </div>

              <div className="mb-2 text-xs text-concrete">
                {e.params.preset} · début {e.params.startTime}s · durée{" "}
                {e.params.duration}s
              </div>

              {e.status === "ready" && e.resultUrl && (
                <div className="space-y-2">
                  <video
                    src={e.resultUrl}
                    controls
                    className="w-full max-w-xs rounded-md bg-black"
                  />
                  <a
                    href={e.resultUrl}
                    download
                    className="inline-block text-sm text-charcoal underline hover:text-black"
                  >
                    Télécharger
                  </a>
                </div>
              )}

              {e.status === "error" && (
                <div className="rounded-md bg-red-50 px-3 py-2 text-xs text-red-700">
                  {e.errorMessage || "Erreur inconnue"}
                </div>
              )}
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}
